import { UpRightButton } from "@/client/components/up-right-button";
import { type CreditCard, type Offer } from "@/data/schemas";
import { getOfferFirstYearValue, getPercentReturn } from "@/utils/calculations";
import { parseDate } from "@/utils/date";
import { enumToText } from "@/utils/enums";
import { Anchor, Box, Card, Group, Text, useMantineTheme } from "@mantine/core";
import { IconQuestionMark } from "@tabler/icons-react";
import va from "@vercel/analytics";
import { format } from "date-fns";
import { useMemo } from "react";

export const OfferView = ({
  offer,
  parent,
}: {
  offer: Offer;
  parent: CreditCard;
}) => {
  const theme = useMantineTheme();
  const offerFirstYearValue = useMemo(
    () => getOfferFirstYearValue(offer, parent),
    [offer, parent],
  );
  const percentReturn = getPercentReturn(offer, parent);

  return (
    <Card
      p={"xs"}
      withBorder={true}
      style={{ borderLeft: `5px ${theme.colors.blue[5]} solid` }}
    >
      <Group justify={"space-between"}>
        <Box>
          <Text size={"sm"}>
            <b>
              {offer.amount
                .map(
                  (amount) =>
                    `${
                      (amount.currency ?? parent.currency) === "USD"
                        ? amount.amount.toLocaleString()
                        : `${amount.amount / 1000}k`
                    } ${enumToText(amount.currency ?? parent.currency)}`,
                )
                .join(" + ")}
            </b>
          </Text>
          <Text size={"sm"}>
            ${offer.spend.toLocaleString()} in {offer.days}d | {percentReturn}%
            Return
          </Text>
          {offer.details && <Text size={"sm"}>{offer.details}</Text>}
          {offer.expiration &&
            Date.now() > parseDate(offer.expiration).getTime() && (
              <Text size={"sm"}>
                {`Expired ${format(parseDate(offer.expiration), "MMM y")}`}
              </Text>
            )}
          {offer.expiration &&
            Date.now() <= parseDate(offer.expiration).getTime() && (
              <Text size={"sm"}>
                {`Expires ${format(parseDate(offer.expiration), "MMM dd, y")}`}
              </Text>
            )}
        </Box>
        <Box right="right" size={"sm"} style={{ textAlign: "right" }}>
          <Group gap={"5px"} justify="right">
            <IconQuestionMark
              size={14}
              style={{ marginLeft: "3px", cursor: "pointer" }}
              onClick={() => alert(offerFirstYearValue.explanation)}
            />
            <Text size={"lg"}>${offerFirstYearValue.value}</Text>
          </Group>
          <Text>
            {
              <Anchor
                href={offer.url ?? parent.url}
                target={"_blank"}
                size={"sm"}
                onClick={() => {
                  va.track("Offer Click", {
                    card: `${parent.issuer} ${parent.name}`,
                  });
                }}
              >
                Link <UpRightButton />
              </Anchor>
            }
          </Text>
          <Text>
            {offer.referralUrl && (
              <Anchor
                href={offer.referralUrl}
                onClick={() => {
                  va.track("Referral Click", {
                    card: `${parent.issuer} ${parent.name}`,
                  });
                }}
                target={"_blank"}
                size={"sm"}
              >
                Referral Link
                <UpRightButton />
              </Anchor>
            )}
          </Text>
        </Box>
      </Group>
    </Card>
  );
};
